import { useMemo, useRef } from "react";
import { useVideoUI } from "@platform/store";
import { VideoMeetingActiveTab } from "../../videoMeeting.types";
import { VideoMeetingSidebar } from "./VideoMeetingSidebar";
import {
  ACTION_STATUS,
  useUpdateActionStatus,
  type Action,
} from "@core/features/actions";
import { useChatUnreadCount } from "@core/features/chat";
import { useFetchUserCircles } from "@core/features/circle";
import { useMeetingChatConversationId } from "@platform/hooks/useMeetingChatConversationId";
import { usePlatformStore } from "@platform/store";

export type Props = {
  priorityBasedPolicy: boolean;
  sharingAttendeeId: string;
  meetingScriptAction: Action | null;
};

export function VideoMeetingSidebarContainer({
  priorityBasedPolicy,
  sharingAttendeeId,
  meetingScriptAction,
}: Props) {
  const { activeTab, scrollPositions, closeSidebar, setActiveTab, setScrollPosition } = useVideoUI();

  const meetingConversationId = useMeetingChatConversationId();
  const allowedConversationIds = useMemo(
    () => (meetingConversationId ? [meetingConversationId] : []),
    [meetingConversationId]
  );
  const unreadCount = useChatUnreadCount(allowedConversationIds);
  const user = usePlatformStore((s) => s.user);
  const { data: circlesData } = useFetchUserCircles({ user });
  const userCircles = circlesData?.circles ?? null;

  const { mutate: markMeetingScriptCompleted } = useUpdateActionStatus({
    actionId: meetingScriptAction?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: meetingScriptAction?.current_status?.name,
  });

  const hasMarkedMeetingScriptRef = useRef(false);

  const handleClose = () => {
    closeSidebar();
  };

  const handleTabChange = (tab: VideoMeetingActiveTab) => {
    setActiveTab(tab);

    if (
      tab === "materials" &&
      meetingScriptAction &&
      !hasMarkedMeetingScriptRef.current
    ) {
      hasMarkedMeetingScriptRef.current = true;
      markMeetingScriptCompleted();
    }
  };

  const handleScrollPositionSave = (
    tab: VideoMeetingActiveTab,
    position: number
  ) => {
    setScrollPosition(tab, position);
  };

  return (
    <VideoMeetingSidebar
      activeTab={activeTab}
      scrollPositions={scrollPositions}
      priorityBasedPolicy={priorityBasedPolicy}
      sharingAttendeeId={sharingAttendeeId}
      onClose={handleClose}
      onTabChange={handleTabChange}
      onScrollPositionSave={handleScrollPositionSave}
      meetingScriptAction={meetingScriptAction}
      unreadCount={unreadCount}
      userCircles={userCircles}
    />
  );
}

export default VideoMeetingSidebarContainer;
